// @flow
import React, { useRef, useMemo, useState, useEffect } from 'react';
import { type FieldProps } from 'formik';
import { kebabCase } from 'lodash';
import moment from 'moment';
import classNames from 'classnames';
import { focusNextInput, getValidationStatus } from 'Components/frameworks/formik/inputsUtils';
import { BirthDateInputSeparator } from './BirthDateInputSeparator.jsx';
import { Legend } from './Legend.jsx';

type Props = {
  dataTestId?: string,
  label?: string,
  isRequired: boolean,
  isDisabled: boolean,
  dayPlaceholder: string,
  monthPlaceholder: string,
  yearPlaceholder: string,
  dateFormat: string,
  ...FieldProps,
};

export const DateInput = ({
  dataTestId,
  label,
  field,
  isRequired,
  isDisabled,
  form,
  ...props
}: Props) => {
  const { isValidated, isErrored, isDefaultState } = getValidationStatus(field, form, isDisabled);
  const { current: uniqIdentifier } = useRef(`dateInput-${field.name}`);
  const refFirstInput = useRef(null);

  // Form state (global)
  const date = useMemo(() => (field.value ? moment(field.value) : null), [field.value]);
  const dateDay = date && date.isValid() ? date.format('DD') : '';
  const dateMonth = date && date.isValid() ? date.format('MM') : '';
  const dateYear = date && date.isValid() ? date.format('YYYY') : '';

  // Field state (local)
  const [day, setDay] = useState(dateDay);
  const [month, setMonth] = useState(dateMonth);
  const [year, setYear] = useState(dateYear);

  const onChangeDay = ({ target }) => {
    setDay(target.value);
    focusNextInput(target);
  };

  const onChangeMonth = ({ target }) => {
    setMonth(target.value);
    focusNextInput(target);
  };

  const onChangeYear = ({ target }) => {
    setYear(target.value);
    focusNextInput(target);
  };

  const structure = [
    {
      keyPos: 'd',
      name: `${field.name}-BirthdateInput-day`,
      value: day,
      title: 'Day',
      length: 2,
      handleChange: onChangeDay,
      placeholder: props.dayPlaceholder,
    },
    {
      keyPos: 'm',
      name: `${field.name}-BirthdateInput-month`,
      value: month,
      title: 'Month',
      length: 2,
      handleChange: onChangeMonth,
      placeholder: props.monthPlaceholder,
    },
    {
      keyPos: 'y',
      name: `${field.name}-BirthdateInput-year`,
      value: year,
      title: 'Year',
      length: 4,
      handleChange: onChangeYear,
      placeholder: props.yearPlaceholder,
    },
  ].sort(
    // sort each input to respect the locale format (DD/MM/YYYY or YYYY/MM/DD ...)
    (i1, i2) =>
      props.dateFormat.toLowerCase().indexOf(i1.keyPos) -
      props.dateFormat.toLowerCase().indexOf(i2.keyPos),
  );

  // Update form state on field change
  useEffect(
    () => {
      if (structure.every(({ value, length }) => value.length === length)) {
        const birthdate = moment([+year, +month - 1, +day]).valueOf();
        form.setFieldValue(field.name, birthdate);
      }
    },
    [day, month, year],
  );

  // Update field state on form change
  useEffect(
    () => {
      if (dateDay !== day) {
        setDay(dateDay);
      }
      if (dateMonth !== month) {
        setMonth(dateMonth);
      }
      if (dateYear !== year) {
        setYear(dateYear);
      }
    },
    [field.value],
  );

  const handleBlur = ({ relatedTarget }) => {
    if (
      !relatedTarget ||
      !relatedTarget.dataset.dateinputidentifier ||
      !relatedTarget.dataset.dateinputidentifier.includes('uniqIdentifier')
    ) {
      form.setFieldTouched(field.name);
    }
  };

  const handleFocus = ({ target }) => {
    if (
      structure.every(({ value }) => value === '') &&
      refFirstInput.current &&
      target !== refFirstInput.current
    ) {
      refFirstInput.current.focus();
    }
  };

  return (
    <fieldset
      className={classNames('flex flex-col border-0 m-0 p-0', {
        'opacity-50 italic': isDisabled,
      })}
    >
      {!!label && <Legend label={label} isDisabled={isDisabled} isRequired={isRequired} />}
      <div
        data-testid={dataTestId ? `wrapper-${dataTestId}` : undefined}
        className={classNames(
          'border-1 flex items-stretch relative rounded-tiny transition-all w-full',
          {
            'border-emerald': isValidated,
            'border-red formik-errored': isErrored,
            'border-gray-light focus:border-blue': isDefaultState,
            'bg-gray-lighter border-gray-lighter': isDisabled,
          },
        )}
      >
        {structure.map(({ name, handleChange, value, length, placeholder, title }, key) => (
          <input
            className={classNames(
              'bg-transparent leading-normal outline-none overflow-hidden py-2 text-md sm:text-base text-center text-gray-darker w-full',
              {
                'cursor-not-allowed': isDisabled,
              },
            )}
            autoComplete="off"
            ref={key === 0 ? refFirstInput : undefined}
            key={name}
            name={name}
            type="tel"
            title={title}
            onChange={handleChange}
            onBlur={handleBlur}
            onFocus={handleFocus}
            maxLength={length}
            value={value}
            placeholder={placeholder}
            disabled={isDisabled}
            data-testid={`${dataTestId || kebabCase(field.name)}-${title.toLowerCase()}`}
            data-dateinputidentifier={uniqIdentifier}
          />
        ))}
        <BirthDateInputSeparator position={1} isValidated={isValidated} isErrored={isErrored} />
        <BirthDateInputSeparator position={2} isValidated={isValidated} isErrored={isErrored} />
      </div>
    </fieldset>
  );
};
